import { ethers } from 'ethers';
import { gql, GraphQLClient } from 'graphql-request';

import { getContractAddress } from '~config/protocol';
import { NetworkIds } from '~config/rpcConfig';
import { EVestaAsset } from '~config/types';
import { calcUserAssetGain, calcUserRewardGain } from '~services/contracts/stabilityPool';

export default class StabilityPool {
  constructor(private readonly client: GraphQLClient, private readonly networkId: NetworkIds) {}

  async getStabilityPoolTotalDeposits(asset: EVestaAsset) {
    const stabilityPoolTotalDepositsQuery = gql`
      query get_total_deposits($asset: String) {
        assetStats(first: 1, where: { asset_: { name: $asset } }) {
          currentAssetSystemState {
            tokensInStabilityPool
          }
        }
      }
    `;

    const res: {
      assetStats: { currentAssetSystemState: { tokensInStabilityPool: string } }[];
    } = await this.client.request(stabilityPoolTotalDepositsQuery, { asset });

    return res.assetStats.length > 0 ? res.assetStats[0].currentAssetSystemState.tokensInStabilityPool : '0';
  }

  async getUserStabilityPoolRewards({ asset, user }: { asset: EVestaAsset; user: string }) {
    const assetAddress = getContractAddress(asset, this.networkId).toLowerCase();
    const stabilityDepositId = `${user}-${assetAddress}`.toLowerCase();

    const userStabilityPoolRewardsQuery = gql`
      query get_user_stability_pool_rewards($asset: String, $stabilityDepositId: String) {
        asset(id: $asset) {
          spSFactor
          spGFactor
        }
        stabilityDeposit(id: $stabilityDepositId) {
          currentG
          currentP
          currentS
          depositedAmount
        }
      }
    `;

    const {
      asset: { spGFactor, spSFactor },
      stabilityDeposit,
    }: {
      asset: { spSFactor: string; spGFactor: string };
      stabilityDeposit: {
        currentG: string;
        currentP: string;
        currentS: string;
        depositedAmount: string;
      } | null;
    } = await this.client.request(userStabilityPoolRewardsQuery, {
      asset: assetAddress,
      stabilityDepositId,
    });

    let currentG = '0';
    let currentP = '1';
    let currentS = '0';
    let depositedAmount = '0';

    if (stabilityDeposit) {
      currentG = ethers.utils.formatEther(stabilityDeposit.currentG);
      currentP = ethers.utils.formatEther(stabilityDeposit.currentP);
      currentS = ethers.utils.formatEther(stabilityDeposit.currentS);
      depositedAmount = stabilityDeposit.depositedAmount;
    }

    const rewardGains = calcUserRewardGain({
      globalG: ethers.utils.formatEther(spGFactor),
      userG: currentG,
      userDeposit: depositedAmount,
      userP: currentP,
    });

    const assetGains = calcUserAssetGain({
      globalS: ethers.utils.formatEther(spSFactor),
      userS: currentS,
      userDeposit: depositedAmount,
      userP: currentP,
    });

    return {
      rewardGains,
      assetGains,
      userDeposit: depositedAmount,
    };
  }
}
